import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useProjectDetail } from '@/hooks/useProjectDetail';
import { useProjectPermissions } from '@/hooks/useProjectPermissions';
import { Skeleton } from '@/components/ui/skeleton';
import NotFound from '@/pages/NotFound';
import ProjectCompactHeader from './ProjectCompactHeader';
import ProjectQuickActions from './ProjectQuickActions';
import OptimizedProjectContent from './OptimizedProjectContent';
import ProjectExitManager from './ProjectExitManager';

interface ProjectDetailPageProps {
  projectId: string;
}

const ProjectDetailPage = ({ projectId }: ProjectDetailPageProps) => {
  const queryClient = useQueryClient();
  const { data: project, isLoading: projectLoading, error } = useProjectDetail(projectId);
  const { data: permissions, isLoading: permissionsLoading } = useProjectPermissions(projectId);

  // ✅ Phase 3: Cleanup queries on component unmount (when navigating away)
  useEffect(() => {
    return () => {
      // Cancel ongoing project-specific queries to prevent cascade
      queryClient.cancelQueries({ queryKey: ['project-detail', projectId] });
      queryClient.cancelQueries({ queryKey: ['specs-data', projectId] });
      queryClient.cancelQueries({ queryKey: ['diary-data', projectId] });
      queryClient.cancelQueries({ queryKey: ['diary-specs', projectId] });
      queryClient.cancelQueries({ queryKey: ['diary-photos', projectId] });
      queryClient.cancelQueries({ queryKey: ['diary-pins', projectId] });
      queryClient.cancelQueries({ queryKey: ['pin-image-counts', projectId] });
      
      if (import.meta.env.DEV) {
        console.log(`🧹 Cleaned up queries for project ${projectId}`);
      }
    };
  }, [projectId, queryClient]);

  if (projectLoading || permissionsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !project) {
    return <NotFound />;
  }


  return (
    <div className="space-y-3">
      <ProjectExitManager projectId={projectId} />
      
      <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3">
        <ProjectCompactHeader project={project} />
        <ProjectQuickActions project={project} permissions={permissions} />
      </div>
      
      <OptimizedProjectContent project={project} permissions={permissions} />
    </div>
  );
};

export default ProjectDetailPage;